import { Color, Spacing } from '@persona-id/design-tokens';
import { ArrowLeftIcon, ArrowRightIcon, PlusIcon } from '@persona-id/design-tokens/icons';
import { isEqual } from 'lodash';
import { capitalize } from 'lodash/fp';
import { FC, useCallback, useMemo, useReducer, useState } from 'react';
import OutsideClickHandler from 'react-outside-click-handler';
import styled, { css } from 'styled-components';

import { ErrorType } from 'dashboard/components/PlatformSchemaEditor/constants';
import { ErrorDisplay } from 'dashboard/components/PlatformSchemaEditor/ErrorDisplay';
import SchemaProperties from 'dashboard/components/PlatformSchemaEditor/SchemaProperties';
import { createNewProperty } from 'dashboard/components/PlatformSchemaEditor/utils';
import MaximizablePanel from 'dashboard/components-lib/MaximizablePanel';
import { PlatformJsonSchema } from 'dashboard/lib/workflows/platform-object';
import { WorkflowFieldObjectKey } from 'dashboard/models/workflow';
import Button from 'design-system/components/Button';
import Spacer from 'design-system/components/Spacer';
import Table, { TableHead, TableHeaderCell, TableRow } from 'design-system/components/Table';
import useUndoableUpdate from 'lib/hooks/useUndoableUpdate';
import { JsonSchemaObject } from 'lib/json-schema';

const CONTAINER_STYLE = css`
  padding-left: ${Spacing[1]};
  padding-right: ${Spacing[1]};
`;

const FixedTable = styled(Table)`
  table-layout: fixed;
`;

const FlexContainer = styled.div`
  display: flex;
`;

const FixedTableHeaderCell = styled(TableHeaderCell)`
  width: 100px;
`;

const ActionsContainer = styled(FlexContainer)`
  border-top: 1px solid ${Color.CoreSlate100};
  justify-content: space-between;
  padding: ${Spacing[3]} 0;
`;

const NullStateContainer = styled.div`
  text-align: center;
`;

interface PlatformSchemaEditorProps {
  firstColumnName?: string;
  jsonSchema: JsonSchemaObject<PlatformJsonSchema>;
  nestLevel?: number;
  panelSubtitle?: string;
  panelTitle: string;
  includePersonaObjects: boolean;
  disableOutsideClick?: boolean;
  templateOptionsByObjectKey: Partial<
    Record<WorkflowFieldObjectKey, { label: string; value: string }[]>
  >;
  onClosePanel: () => void;
  updateJsonSchema: (jsonSchema: JsonSchemaObject<PlatformJsonSchema>) => void;
}

const PlatformSchemaEditor: FC<PlatformSchemaEditorProps> = ({
  firstColumnName = 'property',
  jsonSchema,
  panelSubtitle,
  panelTitle,
  disableOutsideClick = false,
  onClosePanel,
  updateJsonSchema,
  ...props
}) => {
  const [editableId, setEditableId] = useState<string | undefined>();
  const [outsideClickable, setOutsideClickable] = useState<boolean>(true);
  // Since the footer actions are outside of the table component, and they are trying to modify state
  // that's managed internally within the table component, we need to force rerender the table
  const [rerenderTrigger, forceRerender] = useReducer((x) => x + 1, 0);
  const {
    handleChangeWithUndoRedo: updateJsonSchemaWithUndo,
    undo,
    redo,
    canUndo,
    canRedo,
    currentValue: currentJsonSchema,
  } = useUndoableUpdate(jsonSchema, updateJsonSchema);
  const isEmpty = useMemo(
    () => !Object.keys(currentJsonSchema.properties || {}).length,
    [currentJsonSchema.properties],
  );
  const [errors, setErrors] = useState<Record<string, ErrorType[]>>({});

  const addError = useCallback(
    (id: string, errorsForId: ErrorType[]) => {
      if (!isEqual(errors[id], errorsForId)) {
        setErrors({
          ...errors,
          [id]: errorsForId,
        });
      }
    },
    [errors],
  );
  const footer = (
    <ActionsContainer>
      <Button
        aria-label='Add new property'
        id='platform-schema-editor__add-property'
        variant='basic'
        size='md'
        width='auto'
        prefixIcon={<PlusIcon />}
        onClick={() => {
          const newProp = createNewProperty();
          updateJsonSchemaWithUndo({
            ...currentJsonSchema,
            properties: {
              ...currentJsonSchema.properties,
              [newProp.name]: newProp.schema,
            },
            required: [...(currentJsonSchema.required || []), newProp.name],
          });
          setEditableId(newProp.id);
          forceRerender();
        }}
      >
        Add {firstColumnName}
      </Button>
      <FlexContainer>
        <Button
          aria-label='Undo schema change'
          id='platform-schema-editor__undo'
          variant='basic'
          size='md'
          width='auto'
          disabled={!canUndo}
          prefixIcon={<ArrowLeftIcon />}
          onClick={() => {
            undo();
            forceRerender();
          }}
        >
          Undo
        </Button>
        <Spacer width={1} height={0} />
        <Button
          aria-label='Redo schema change'
          id='platform-schema-editor__redo'
          variant='basic'
          size='md'
          width='auto'
          disabled={!canRedo}
          suffixIcon={<ArrowRightIcon />}
          onClick={() => {
            redo();
            forceRerender();
          }}
        >
          Redo
        </Button>
      </FlexContainer>
    </ActionsContainer>
  );

  return (
    <div id='platform-schema-editor__panel'>
      <MaximizablePanel
        title={panelTitle}
        subtitle={panelSubtitle}
        containerStyleOverride={CONTAINER_STYLE}
        outsideExceptionalIdPrefixes={['platform-schema-editor__']}
        onOutsideClickAndEscPress={() => {
          if (!disableOutsideClick && outsideClickable) {
            onClosePanel();
          }
        }}
        onClosePanel={onClosePanel}
        footer={footer}
      >
        <OutsideClickHandler onOutsideClick={() => setEditableId(undefined)}>
          <ErrorDisplay errors={errors} />
          <FixedTable>
            <TableHead
              id='platform-schema-editor__table-head'
              onClick={() => setEditableId(undefined)}
            >
              <TableRow>
                <TableHeaderCell>{capitalize(firstColumnName)}</TableHeaderCell>
                <TableHeaderCell>Type</TableHeaderCell>
                <FixedTableHeaderCell>Required</FixedTableHeaderCell>
              </TableRow>
            </TableHead>
            {!isEmpty && (
              <SchemaProperties
                {...props}
                key={rerenderTrigger}
                editableId={editableId}
                jsonSchema={currentJsonSchema}
                setEditableId={setEditableId}
                setOutsideClickable={setOutsideClickable}
                updateJsonSchema={updateJsonSchemaWithUndo}
                addError={addError}
              />
            )}
          </FixedTable>
          {isEmpty && (
            <NullStateContainer>
              <Spacer height={3} />
              <div>No properties configured.</div>
              <Spacer height={5} />
            </NullStateContainer>
          )}
        </OutsideClickHandler>
      </MaximizablePanel>
    </div>
  );
};

export default PlatformSchemaEditor;
